import React, { useMemo, type ReactNode } from 'react'
import { appHistory } from '@core/Utils/routing/history'
import { isReactNativeApp, sendToNative } from '@mobi/web-native-comms/web'
import { HostProvider, type HostApi } from '@mobi/account/Common/HostContext'
import { trackOptimoveEvent } from '@core/Services/Optimove/optimove'
import { DepositFlow } from '@mobi/account/Areas/Deposit/typings/types'
import { ClearInsufficientFundsForBet } from '@core/Areas/Quickbet/signals'
import { getFeatureValue } from '@core/State/FeatureFlags/helpers/getFeatureValue'
import { setQualtricsIntercept } from '@core/Areas/Qualtrics/qualtrics'

type Params = {
  history: typeof appHistory
}

const createHostApi = ({ history }: Params): HostApi => ({
  features: {
    hasFeature: featureKey => {
      return new Promise(resolve => {
        const val = getFeatureValue(featureKey)
        resolve(val as PromiseLike<boolean>)
      })
    },
  },
  events: {
    onDepositSuccess: (flow: DepositFlow, amount?: number) => {
      if (!amount) return
      if (flow === 'quick-deposit') {
        ClearInsufficientFundsForBet()
      }
      trackOptimoveEvent({
        eventName: 'money_deposited',
        data: {
          amount,
        },
      })
      setQualtricsIntercept('deposit')
    },
    findTab: () => {
      if (isReactNativeApp) {
        sendToNative('REQUEST_NATIVE_NAVIGATION', { location: 'TAB Locator' })
      } else {
        history.push('/tablocator')
      }
    },
  },
  channel: 'mobile',
})

type Props = {
  children: MaybeArray<ReactNode>
}

export const HostContextMobile = ({ children }: Props): JSX.Element => {
  // TODO: Remove this when CreditCardForm is more resilient
  // This is a memo to ensure the `analytics` object (see above) remains the same
  // as that is checked when adding a card with Braintree. Not having this can
  // cause Braintree's hosted fields to crash when anything in the parent
  // has changed.
  const api = useMemo(
    () =>
      createHostApi({
        history: appHistory,
      }),
    []
  )

  return <HostProvider api={api}>{children}</HostProvider>
}
